import React, {
  useCallback,
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { PICKUP_STATUSES } from '../../constants';

const MultipleSelect = ({
  id, status, handleUpdateStatus, lockedStatus,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const selectedStatusesRef = useRef(Array.isArray(status) ? [...status] : []);
  const [selectedStatuses, setSelectedStatuses] = useState(Array.isArray(status) ? [...status] : []);

  useEffect(() => {
    setSelectedStatuses(Array.isArray(status) ? [...status] : []);
    selectedStatusesRef.current = Array.isArray(status) ? [...status] : [];
  }, [status]);

  const label = useMemo(() => {
    if (!selectedStatuses.length) return 'Select status';
    if (selectedStatuses.length === 1) return (PICKUP_STATUSES.find(s => s.id === selectedStatuses[0])?.text || selectedStatuses[0]);
    return `${selectedStatuses.length} selected`;
  }, [selectedStatuses]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!open) return;
      if (!containerRef.current) return;
      if (containerRef.current.contains(e.target)) return;

      const prev = Array.isArray(status) ? [...status] : [];

      if (
        prev.length !== selectedStatusesRef.current.length
        || !prev.every(p => selectedStatusesRef.current.includes(p))
      ) {
        handleUpdateStatus(id, [...selectedStatusesRef.current]);
      }

      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, id, status]);

  const onSelectStatus = useCallback((item, checked) => () => setSelectedStatuses((prev) => {
    const next = checked ? prev.filter(v => v !== item.id) : [...prev, item.id];

    selectedStatusesRef.current = next;

    return next;
  }), []);

  return (
    <div ref={containerRef}>
      <select
        className="form-control"
        style={{ minWidth: '250px' }}
        value="__multi__"
        disabled={lockedStatus}
        onMouseDown={(e) => {
          e.preventDefault();
          if (lockedStatus) return;
          setOpen(v => !v);
        }}
        onChange={() => {}}
      >
        <option value="__multi__">{label}</option>
      </select>

      {open && (
        <div
          className="dropdown-menu show"
          style={{
            display: 'block',
            position: 'absolute',
            top: 'calc(100% + 2px)',
            left: 0,
            minWidth: '250px',
            maxHeight: '260px',
            overflowY: 'auto',
            padding: '6px 0',
            zIndex: 1000,
          }}
        >
          {PICKUP_STATUSES.map((item) => {
            const checked = selectedStatuses.includes(item.id);
            return (
              <button
                type="button"
                key={item.id}
                className="dropdown-item"
                onClick={onSelectStatus(item, checked)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                  background: 'transparent',
                  border: 0,
                  padding: '6px 12px',
                  textAlign: 'left',
                }}
              >
                <span>{item.text}</span>
                <span className={`fa ${checked ? 'fa-check' : ''}`} style={{ width: '14px' }} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SingleSelect = ({
  id, status, handleUpdateStatus, lockedStatus, addEmpty,
}) => (
  <select
    className="form-control"
    value={status}
    onChange={e => !lockedStatus && handleUpdateStatus(id, e.target.value)}
  >
    {addEmpty && <option value="">Select status</option>}
    {PICKUP_STATUSES.map(item => (
      <option key={item.id} value={item.id}>{item.text}</option>
    ))}
  </select>
);

const StatusDropdown = ({
  multiple, lockedStatus, switchLockStatus, onHistoryClick, ...props
}) => (
  <div
    style={{
      minWidth: '250px', display: 'flex', alignItems: 'center', gap: '8px', position: 'relative',
    }}
  >
    {
      multiple
        ? <MultipleSelect lockedStatus={lockedStatus} {...props} />
        : <SingleSelect lockedStatus={lockedStatus} {...props} />
    }
    {onHistoryClick && (
      <button
        type="button"
        className="fa fa-list-alt"
        onClick={onHistoryClick}
        style={{ border: 'none', backgroundColor: 'transparent' }}
      />
    )}
    { lockedStatus != null && (
      <button
        type="button"
        onClick={switchLockStatus}
        className={`fa ${lockedStatus ? 'fa-lock' : 'fa-unlock-alt'}`}
        style={{ border: 'none', backgroundColor: 'transparent' }}
      />
    )}
  </div>
);

const baseProps = {
  // eslint-disable-next-line react/forbid-prop-types
  id: PropTypes.any,
  addEmpty: PropTypes.bool,
  handleUpdateStatus: PropTypes.func.isRequired,
  lockedStatus: PropTypes.bool,
  status: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Array),
  ]).isRequired,
};

const baseDefaultProps = {
  id: null,
  addEmpty: false,
  lockedStatus: null,
};

MultipleSelect.propTypes = { ...baseProps };

MultipleSelect.defaultProps = { ...baseDefaultProps };

SingleSelect.propTypes = { ...baseProps };

SingleSelect.defaultProps = { ...baseDefaultProps };

StatusDropdown.propTypes = {
  ...baseProps,
  switchLockStatus: PropTypes.func,
  multiple: PropTypes.bool,
  onHistoryClick: PropTypes.func,
};

StatusDropdown.defaultProps = {
  ...baseDefaultProps,
  switchLockStatus: () => {},
  multiple: false,
  onHistoryClick: null,
};

export default StatusDropdown;
